import { useState } from "react";
import { Box, Button, Stack, TextField, Typography, useTheme } from "@mui/material";
import CommonBottomSheet from "@/modules/common/components/ui-kit/bottom-sheets/common-bottom-sheet";
import ModalHeader from "@/modules/common/components/ui-kit/modals/modal-header";
import ModalFooter from "@/modules/common/components/ui-kit/modals/modal-footer";
import { TOAST_IDS, ToastType } from "@/modules/common/types";
import { showToast } from "@/modules/common/utils";
import { useLeadContext } from "@/modules/lead-page/context/lead-page.context";
import { editJourneyStatus } from "@/modules/lead-page/api/lead-page.v2.api";
import { getJourneyStatuses } from "@/modules/leads/api/index.v2";
import { addTrackComment } from "@/modules/property-tracks/api";
import { TrackCommentSource } from "@/modules/property-tracks/dtos";
import { Track } from "@/modules/property-tracks/types";
import { queryCommonFlags } from "@/api/constants";
import { queryClient } from "@/queryClient";
import { useQuery } from "@tanstack/react-query";
import { Phone, PhoneMissed } from "lucide-react";

const FOLLOW_UP_OPTIONS = {
  answer: { label: "Answer", statusName: "[unlocked] engaged" },
  no_answer: { label: "No Answer", statusName: "[unlocked] no answer" },
} as const;

type FollowUpType = keyof typeof FOLLOW_UP_OPTIONS;

function FollowUpMobile({ track }: Readonly<{ track: Track }>) {
  const theme = useTheme();
  const { lead } = useLeadContext();

  const [selectedType, setSelectedType] = useState<FollowUpType | null>(null);
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const journeyUId = lead?.data?.lead?.journey_uid;

  const { data: statusesData } = useQuery({
    queryKey: ["getJourneyStatuses", journeyUId],
    queryFn: () =>
      journeyUId ? getJourneyStatuses(journeyUId) : Promise.resolve(null),
    ...queryCommonFlags,
    enabled: !!journeyUId,
  });

  const handleSelect = (type: FollowUpType) => {
    setSelectedType(type);
    setComment("");
  };

  const handleClose = () => {
    setSelectedType(null);
    setComment("");
  };

  const handleSubmit = async () => {
    if (!selectedType || !journeyUId) return;

    const statusName = FOLLOW_UP_OPTIONS[selectedType].statusName;
    const targetStatus = statusesData?.data?.find(
      (s) => s.name.toLowerCase() === statusName.toLowerCase()
    );

    if (!targetStatus) {
      showToast({
        type: ToastType.ERROR,
        message: `Status "${statusName}" not found`,
        toastId: TOAST_IDS.EDIT_LEAD_ERROR,
        options: { position: "bottom-left", autoClose: 3000 },
      });
      return;
    }

    const userComment = comment.trim();
    const trackActivityComment = [
      `• Status: ${targetStatus.name}`,
      ...(userComment ? [`• Note: ${userComment}`] : []),
    ].join("\n");

    setIsSubmitting(true);

    try {
      // Update lead status (logs to lead activity)
      await editJourneyStatus(journeyUId, {
        statusUId: targetStatus.uid,
        comment: userComment || `Follow Up: ${FOLLOW_UP_OPTIONS[selectedType].label}`,
      });

      // Log single formatted entry to property track activity
      if (track?.uid) {
        await addTrackComment({
          trackUId: track.uid,
          data: {
            comment: trackActivityComment,
            mentions: [],
            source: TrackCommentSource.trackFollowUp,
          },
        });
      }

      queryClient.invalidateQueries({ queryKey: ["getLead"] });
      queryClient.invalidateQueries({ queryKey: ["getComments"] });
      queryClient.invalidateQueries({ queryKey: ["track"] });
      queryClient.invalidateQueries({ queryKey: ["getStatuses"] });
      queryClient.invalidateQueries({ queryKey: ["getStagesByJourney"] });

      showToast({
        type: ToastType.SUCCESS,
        message: "Follow up saved successfully",
        toastId: TOAST_IDS.EDIT_LEAD_SUCCESS,
        options: { position: "bottom-left", autoClose: 3000 },
      });

      handleClose();
    } catch {
      showToast({
        type: ToastType.ERROR,
        message: "Failed to save follow up",
        toastId: TOAST_IDS.EDIT_LEAD_ERROR,
        options: { position: "bottom-left", autoClose: 3000 },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Box
        sx={(t) => ({
          border: `1px solid ${t.nawy.colors.semantic.border.secondary}`,
          backgroundColor: "white",
          borderRadius: t.nawy.radius.xl,
        })}
      >
        <Box
          sx={(t) => ({
            px: t.nawy.spacing.md,
            pt: t.nawy.spacing.xs,
            pb: "4px",
          })}
        >
          <Typography
            variant="inherit"
            sx={{ fontSize: "15px", fontWeight: 600, lineHeight: "22px" }}
          >
            Follow Up
          </Typography>
        </Box>

        <Stack
          direction="row"
          gap={1}
          sx={(t) => ({
            px: t.nawy.spacing.md,
            pt: "4px",
            pb: t.nawy.spacing.xs,
          })}
        >
          <Button
            variant="outlined"
            fullWidth
            onClick={() => handleSelect("answer")}
            startIcon={
              <Phone
                size={16}
                color={theme.nawy.colors.semantic.indicator.green}
              />
            }
            sx={(t) => ({
              borderRadius: t.nawy.radius.lg,
              textTransform: "none",
              fontSize: "13px",
              fontFamily: "inherit",
              borderColor: t.nawy.colors.semantic.border.primary,
              color: t.nawy.colors.semantic.text.primary,
              "&:hover": {
                borderColor: t.nawy.colors.semantic.indicator.green,
                backgroundColor: "transparent",
              },
            })}
          >
            Answer
          </Button>

          <Button
            variant="outlined"
            fullWidth
            onClick={() => handleSelect("no_answer")}
            startIcon={
              <PhoneMissed
                size={16}
                color={theme.nawy.colors.semantic.indicator.redDark}
              />
            }
            sx={(t) => ({
              borderRadius: t.nawy.radius.lg,
              textTransform: "none",
              fontSize: "13px",
              fontFamily: "inherit",
              borderColor: t.nawy.colors.semantic.border.primary,
              color: t.nawy.colors.semantic.text.primary,
              "&:hover": {
                borderColor: theme.nawy.colors.semantic.indicator.redDark,
                backgroundColor: "transparent",
              },
            })}
          >
            No Answer
          </Button>
        </Stack>
      </Box>

      <CommonBottomSheet
        isOpen={!!selectedType}
        onClose={handleClose}
        snapPoints={[0.5]}
      >
        <ModalHeader
          title={selectedType ? FOLLOW_UP_OPTIONS[selectedType].label : ""}
          onClose={handleClose}
        />
        <Stack gap={2} width="100%" p={2}>
          <TextField
            fullWidth
            label="Comment"
            placeholder="Add a comment (optional)"
            multiline
            rows={4}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            disabled={isSubmitting}
          />
        </Stack>
        <Box position="sticky" bottom={0}>
          <ModalFooter
            confirmButtonText="Save"
            cancelButtonText="Cancel"
            onConfirm={handleSubmit}
            onCancel={handleClose}
            isLoading={isSubmitting}
          />
        </Box>
      </CommonBottomSheet>
    </>
  );
}

export default FollowUpMobile;
